import { useContext, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate, useParams } from "react-router-dom"

import Actions from "../../components/Configuration/Actions/Actions"
import Loader from "../../components/Loader/Loader"
import { AppContext } from "../../context/AppContext"
import {
  getConfiguratorAreas,
  getAreaSequence,
} from "../../helpers/configuration/areas"
import { getSelectedModel } from "../../hooks/use-selected-model"
import { useMergedViewImage } from "../../hooks/use-merged-view-image"
import SummaryDetail from "./SummaryDetail"

import styles from "./Summary.module.scss"

const Summary = () => {
  const params = useParams()
  const { i18n, t } = useTranslation()
  const navigate = useNavigate()
  const appContext = useContext(AppContext)
  const { store, formikRef } = appContext

  const [image, setImage] = useState(null)
  const [values] = useState(formikRef.current?.values)
  const [imageError, setImageError] = useState(false)

  const selectedModel = store?.chargers.find(c => c.code === params.MODELO)
  const configuratorAreas = getConfiguratorAreas(store.categories)
  const areasOrdered = getAreaSequence(configuratorAreas)
  const model = selectedModel.code
  const currentView = "FRONTAL"
  const imageFolder = store.initConfig.images

  useEffect(() => {
    if (!values && !params.MODELO) {
      navigate(`/${i18n.language}/configure`)
    } else if (!values) {
      navigate(`/${i18n.language}/configure/${params.MODELO}`)
    }
  }, [values, params.MODELO, i18n.language, navigate])

  const { generateMergedImage } = useMergedViewImage({
    model,
    currentView,
    imageFolder,
    originalSize: true,
    sizeFolder: "M",
  })

  const generateImage = async () => {
    if (!values) return
    try {
      const mergedImageBase64 = await generateMergedImage(values)
      setImage(mergedImageBase64)
    } catch (err) {
      setImageError(true)
      console.warn("No se pudo generar la imagen combinada:", err)
    }
  }

  const handleGoToPrev = () => {
    navigate(`/${i18n.language}/configure/${getSelectedModel()}`, { state: { from: "summary", values: values } })
  }

  generateImage()

  return (
    <>
      <div className={styles.formContent}>
        <div className={styles.configuration}>
          <div className={styles.preview}>
            <div className={styles.view}>
              {image && !imageError ? (
                <img className={styles.image} src={image} alt="summaryImage"/>
              ) : !image && !imageError ? (
                <Loader />
              ) : (
                <div className={styles.imageError}>
                  {t("Something went wrong.")}
                </div>
              )}
            </div>
          </div>
          <div className={styles.menu}>
            <div className={styles.content}>
              <h2>{t("Configuration summary")}</h2>
              <div className={styles.chargerTitle}>
                <h1>{selectedModel.title}</h1>
                <button
                  className={[styles.button].join(" ")}
                  type="button"
                  onClick={handleGoToPrev}
                >
                  {/* Left arrow */}
                  <svg width="21" height="16" viewBox="0 0 21 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" clipRule="evenodd" d="M20.9495 8.0004C20.9494 8.25038 20.7466 8.45289 20.4966 8.45271L0.78344 8.4388C0.533455 8.43862 0.330946 8.23583 0.331123 7.98584C0.331299 7.73586 0.534094 7.53335 0.784079 7.53353L20.4972 7.54744C20.7472 7.54762 20.9497 7.75041 20.9495 8.0004Z" fill="white"/>
                    <path fillRule="evenodd" clipRule="evenodd" d="M7.67989 15.3685C7.4989 15.5409 7.21239 15.534 7.03996 15.353L0.0490218 8.01502L7.03914 0.647681C7.2112 0.466333 7.49769 0.458807 7.67904 0.630868C7.86039 0.80293 7.86791 1.08942 7.69585 1.27077L1.29814 8.01373L7.69539 14.7286C7.86783 14.9095 7.86089 15.1961 7.67989 15.3685Z" fill="white"/>
                  </svg>
                </button>
              </div>
              <div className={styles.chargerSubtitle}>
                <h2>{selectedModel.subtitle}</h2>
              </div>
              <div className={styles.summaryDetail}>
                <SummaryDetail
                  areasOrdered={areasOrdered}
                  configuratorAreas={configuratorAreas}
                  values={values}
                />
              </div>
            </div>
          </div>
        </div>
        <Actions summary={true} summaryValues={values} />
      </div>
    </>
  )
}

export default Summary
